<template>
  <div
    class="min-h-screen w-full relative flex flex-col justify-between"
    :style="{
      backgroundImage: `linear-gradient(rgba(10,10,10,0.7),rgba(10,10,10,0.7)), url('${bookingBg}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }"
  >
    <CleanupReservations />
    <div class="flex flex-col justify-center items-center min-h-screen">
      <div
        class="w-full max-w-md bg-black bg-opacity-60 rounded-xl shadow-xl p-8 mt-24 mb-12"
      >
        <h1
          class="text-3xl md:text-4xl font-bold text-[#F3D382] mb-4 tracking-wider text-center"
        >
          {{ $t("booking.title") }}
        </h1>
        <p class="text-white text-center mb-8 opacity-80">
          {{ $t("booking.description") }}
        </p>

        <form @submit.prevent="handleNext" class="flex flex-col gap-6">
          <!-- Name -->
          <label class="flex flex-col text-white">
            {{ $t("booking.name") }}
            <input
              v-model="form.name"
              type="text"
              :placeholder="$t('booking.name')"
              required
              class="mt-2 px-4 py-2 rounded-md border border-[#F3D382] bg-transparent text-white placeholder-white placeholder-opacity-60 focus:outline-none focus:ring-2 focus:ring-[#F3D382]"
            />
          </label>

          <!-- Phone -->
          <label class="flex flex-col text-white">
            {{ $t("booking.phone") }}
            <input
              v-model="form.phone"
              type="tel"
              :placeholder="$t('booking.phone')"
              required
              maxlength="11"
              @input="validatePhone"
              :dir="$i18n && $i18n.locale === 'ar' ? 'rtl' : 'ltr'"
              :class="[
                'mt-2 px-4 py-2 rounded-md border bg-transparent text-white placeholder-white placeholder-opacity-60 focus:outline-none focus:ring-2',
                phoneError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-[#F3D382] focus:ring-[#F3D382]',
              ]"
            />
            <span v-if="phoneError" class="text-red-500 text-xs mt-1">{{
              phoneError
            }}</span>
          </label>

          <!-- Email -->
          <label class="flex flex-col text-white">
            {{ $t("booking.email") }}
            <input
              v-model="form.email"
              type="email"
              :placeholder="$t('booking.email')"
              required
              class="mt-2 px-4 py-2 rounded-md border border-[#F3D382] bg-transparent text-white placeholder-white placeholder-opacity-60 focus:outline-none focus:ring-2 focus:ring-[#F3D382]"
            />
          </label>

          <!-- Guests -->
          <label class="flex flex-col text-white">
            {{ $t("booking.guests") }}
            <input
              v-model="form.guests"
              type="number"
              min="1"
              max="20"
              required
              class="mt-2 px-4 py-2 rounded-md border border-[#F3D382] bg-transparent text-white placeholder-white placeholder-opacity-60 focus:outline-none focus:ring-2 focus:ring-[#F3D382]"
            />
            <span v-if="form.guests > 4" class="text-red-400 text-xs mt-1">
              {{ $t("booking.moreThanFour") }}
            </span>
          </label>

          <!-- Date -->
          <label class="flex flex-col text-white">
            {{ $t("booking.date") }}
            <input
              v-model="form.date"
              type="date"
              :min="getMinDate()"
              required
              class="mt-2 px-4 py-2 rounded-md border border-[#F3D382] bg-transparent text-white focus:outline-none focus:ring-2 focus:ring-[#F3D382] w-full"
              style="color-scheme: dark"
            />
          </label>

          <!-- Submit -->
          <button
            type="submit"
            class="w-full mt-4 py-3 rounded-md border-2 border-[#F3D382] text-[#F3D382] font-bold tracking-wider transition bg-transparent hover:bg-[#F3D382] hover:text-black"
            :disabled="form.guests > 4"
          >
            {{ $t("booking.next") }}
          </button>
        </form>

        <div class="flex justify-center items-center mt-8 gap-3">
          <span class="w-2 h-2 rounded-full bg-[#F5BE32] block"></span>
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import bookingBg from "@/assets/Booking/Booking.jpg";
import { reactive, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import CleanupReservations from "@/components/CleanupReservations.vue";

const router = useRouter();
const auth = getAuth();
const db = getFirestore();

const form = reactive({
  name: "",
  phone: "",
  email: "",
  guests: 2,
  date: "",
});

const phoneError = ref("");

let reservationId = null;
const selectedDuration = localStorage.getItem("duration"); // "breakfast", "lunch", "dinner"

async function fetchUserData() {
  const user = auth.currentUser;
  if (!user) {
    router.push("/login");
    return;
  }

  try {
    // 1. Fetch user profile (only if no saved data)
    const savedName = localStorage.getItem("bookingName");
    const savedPhone = localStorage.getItem("bookingPhone");
    const savedEmail = localStorage.getItem("bookingEmail");

    if (!savedName || !savedPhone || !savedEmail) {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        form.name = data.username || "";
        form.phone = data.phone || "";
        form.email = data.email || "";
      }
    }

    // 2. Find the reservation for this user & selected duration
    reservationId = localStorage.getItem("reservationId");

    if (!reservationId) {
      console.error("No reservation ID found in localStorage.");
      return;
    }

    const reservationRef = doc(db, "reservations", reservationId);
    const reservationSnap = await getDoc(reservationRef);
    if (!reservationSnap.exists()) {
      console.error("Reservation not found in Firestore.");
    }
  } catch (error) {
    console.error("Error fetching user or reservation data:", error);
  }
}

onMounted(() => {
  // Load saved data from localStorage if returning from preorder
  const savedName = localStorage.getItem("bookingName");
  const savedPhone = localStorage.getItem("bookingPhone");
  const savedEmail = localStorage.getItem("bookingEmail");
  const savedGuests = localStorage.getItem("bookingGuests");
  const savedDate = localStorage.getItem("bookingDate");

  if (savedName) form.name = savedName;
  if (savedPhone) form.phone = savedPhone;
  if (savedEmail) form.email = savedEmail;
  if (savedGuests) form.guests = parseInt(savedGuests);
  if (savedDate) form.date = savedDate;

  // Always fetch reservation data
  fetchUserData();
});

async function handleNext() {
  if (!reservationId) {
    console.error("Reservation not found.");
    return;
  }

  // Validate phone number before proceeding
  if (!validatePhone()) {
    return;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      router.push("/login");
      return;
    }

    // Update the reservation
    const reservationRef = doc(db, "reservations", reservationId);
    await updateDoc(reservationRef, {
      name: form.name,
      phone: form.phone,
      email: form.email,
      guests: form.guests,
      date: form.date,
    });

    // Check if user has phone number in their profile, if not save it
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      // If user doesn't have a phone number saved, save the reservation number
      if (!userData.phone || userData.phone.trim() === "") {
        await updateDoc(userDocRef, {
          phone: form.phone,
        });
        console.log("Phone number saved to user profile");
      }
    }

    // Save form data to localStorage for when user returns
    localStorage.setItem("bookingName", form.name);
    localStorage.setItem("bookingPhone", form.phone);
    localStorage.setItem("bookingEmail", form.email);
    localStorage.setItem("bookingGuests", form.guests.toString());
    localStorage.setItem("bookingDate", form.date);
    localStorage.setItem("reservationId", reservationId);
    localStorage.setItem("date", form.date);
    router.push("/preorder");
  } catch (error) {
    console.error("Error updating reservation:", error);
  }
}

function getMinDate() {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  // Get current hour
  const hour = now.getHours();

  // Logic for each duration
  if (selectedDuration === "breakfast") {
    // Breakfast: 8am-12pm
    if (hour >= 12) {
      // Breakfast already passed today
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      return tomorrow.toISOString().slice(0, 10);
    }
    return todayStr;
  }
  if (selectedDuration === "lunch") {
    // Lunch: 12pm-6pm
    if (hour >= 18) {
      // Lunch already passed today
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      return tomorrow.toISOString().slice(0, 10);
    }
    return todayStr;
  }
  if (selectedDuration === "dinner") {
    // Dinner: 6pm-12am
    if (hour >= 24 || hour < 6) {
      // Dinner already passed today (after midnight)
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      return tomorrow.toISOString().slice(0, 10);
    }
    return todayStr;
  }
  // Default: today
  return todayStr;
}

function validatePhone() {
  const phone = form.phone.replace(/\D/g, ""); // Remove non-digits

  if (!phone) {
    phoneError.value = "Phone number is required";
    return false;
  }

  if (phone.length !== 11) {
    phoneError.value = "Phone number must be exactly 11 digits";
    return false;
  }

  // Check if it starts with 01 (Egyptian mobile numbers)
  if (!phone.startsWith("01")) {
    phoneError.value =
      "Please enter a valid Egyptian mobile number (starts with 01)";
    return false;
  }

  phoneError.value = "";
  return true;
}
</script>
